import {
  DINING_CHAIR_BACK_DEPTH_RATIO,
  DINING_CHAIR_DEPTH_M,
  DINING_CHAIR_TABLE_GAP_M,
  DINING_CHAIR_WIDTH_M,
  DINING_PLAIN_TABLE_LAYOUT,
  DINING_RECT_TABLE_LAYOUTS,
  DINING_ROUND_CHAIR_COUNT,
  diningRectSymbolLengthM,
  diningRoundChairOrbitRadiusM,
  diningRoundTableRadiusM,
  type DiningRectLayoutKey,
} from "./diningLayoutConstants";
import {
  diningChairRowCentersM,
  diningRectContentHeightM,
} from "./diningSharedLayout";

export type ChairSizeSvg = {
  depth: number;
  width: number;
  backDepth: number;
};

export type ChairPolylines = {
  seat: number[];
  back: number[];
};

export type GeneratedDiningSvg = {
  svg: string;
  symLength: number;
  symHeight: number;
  viewBoxWidth: number;
  viewBoxHeight: number;
  /** Content bounding box width in SVG units (matches Svg.tsx path bbox scaling). */
  contentWidthSvg: number;
  contentHeightSvg: number;
};

const MARGIN_SVG = 8;
/** Isotropic scale: SVG units per metre on both axes. */
const SVG_UNITS_PER_METRE = 120;

/** Convert metres to SVG units with a uniform scale on both axes. */
export function metresToSvg(metres: number): number {
  return metres * SVG_UNITS_PER_METRE;
}

/** Canonical chair glyph size in SVG units (450 mm x 500 mm). */
export function chairSizeSvg(): ChairSizeSvg {
  const depth = metresToSvg(DINING_CHAIR_DEPTH_M);
  const width = metresToSvg(DINING_CHAIR_WIDTH_M);
  return { depth, width, backDepth: depth * DINING_CHAIR_BACK_DEPTH_RATIO };
}

/** Axis-aligned chair: rectangle seat + back stub (standalone chair glyph). */
export function sideChairPolylines(
  cx: number,
  cy: number,
  side: "left" | "right",
  size: ChairSizeSvg,
): ChairPolylines {
  const { depth, width, backDepth } = size;
  const x0 = cx - depth / 2;
  const x1 = cx + depth / 2;
  const y0 = cy - width / 2;
  const y1 = cy + width / 2;
  const seat = [x0, y0, x1, y0, x1, y1, x0, y1, x0, y0];
  const back =
    side === "left"
      ? [x0, y0, x0 + backDepth, y0, x0 + backDepth, y1, x0, y1, x0, y0]
      : [
          x1 - backDepth,
          y0,
          x1,
          y0,
          x1,
          y1,
          x1 - backDepth,
          y1,
          x1 - backDepth,
          y0,
        ];
  return { seat, back };
}

/** Same glyph rotated so the back faces outward at `outwardAngle` radians. */
export function rotatedChairPolylines(
  cx: number,
  cy: number,
  outwardAngle: number,
  size: ChairSizeSvg,
): ChairPolylines {
  const { depth, width, backDepth } = size;
  const cos = Math.cos(outwardAngle);
  const sin = Math.sin(outwardAngle);
  const rot = (lx: number, ly: number): [number, number] => [
    cx + lx * cos - ly * sin,
    cy + lx * sin + ly * cos,
  ];
  const locals: [number, number][] = [
    [-depth / 2, -width / 2],
    [depth / 2, -width / 2],
    [depth / 2, width / 2],
    [-depth / 2, width / 2],
    [-depth / 2, -width / 2],
  ];
  const seat = locals.flatMap(([lx, ly]) => rot(lx, ly));
  const backLocals: [number, number][] = [
    [depth / 2 - backDepth, -width / 2],
    [depth / 2, -width / 2],
    [depth / 2, width / 2],
    [depth / 2 - backDepth, width / 2],
    [depth / 2 - backDepth, -width / 2],
  ];
  const back = backLocals.flatMap(([lx, ly]) => rot(lx, ly));
  return { seat, back };
}

function fmt(nums: number[]): string {
  return nums.map((n) => Number(n.toFixed(2))).join(" ");
}

function polyline(points: number[]): string {
  return `<polyline class="cls-3" points="${fmt(points)}"/>`;
}

function circlePolyline(cx: number, cy: number, radius: number): number[] {
  const segments = 64;
  const points: number[] = [];
  for (let i = 0; i <= segments; i += 1) {
    const angle = (Math.PI * 2 * i) / segments;
    points.push(cx + Math.cos(angle) * radius, cy + Math.sin(angle) * radius);
  }
  return points;
}

function bboxFromPolylines(polylines: number[][]): {
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
  width: number;
  height: number;
} {
  const pts: [number, number][] = [];
  for (const p of polylines) {
    for (let i = 0; i < p.length; i += 2) pts.push([p[i], p[i + 1]]);
  }
  const minX = Math.min(...pts.map((p) => p[0]));
  const maxX = Math.max(...pts.map((p) => p[0]));
  const minY = Math.min(...pts.map((p) => p[1]));
  const maxY = Math.max(...pts.map((p) => p[1]));
  return {
    minX,
    maxX,
    minY,
    maxY,
    width: maxX - minX,
    height: maxY - minY,
  };
}

function shiftPolyline(p: number[], dx: number, dy: number): number[] {
  const out = [...p];
  for (let i = 0; i < out.length; i += 2) {
    out[i] += dx;
    out[i + 1] += dy;
  }
  return out;
}

function buildSvg(vbW: number, vbH: number, parts: string[]): string {
  const clip = `${-MARGIN_SVG} ${(vbH + MARGIN_SVG).toFixed(2)} ${(vbW + MARGIN_SVG).toFixed(2)} ${(vbH + MARGIN_SVG).toFixed(2)} ${(vbW + MARGIN_SVG).toFixed(2)} ${-MARGIN_SVG} ${-MARGIN_SVG} ${-MARGIN_SVG} ${-MARGIN_SVG} ${(vbH + MARGIN_SVG).toFixed(2)}`;
  return `<svg id="Lager_1" data-name="Lager 1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="0 0 ${vbW.toFixed(2)} ${vbH.toFixed(2)}"><defs><style>.cls-1,.cls-3{fill:#fff;}.cls-2{clip-path:url(#clip-path);}.cls-3{stroke:#000;stroke-linecap:round;stroke-linejoin:round;stroke-width:3px;}</style><clipPath id="clip-path"><polyline class="cls-1" points="${clip}"/></clipPath></defs><g class="cls-2">${parts.join("")}</g></svg>`;
}

function chairRowCenters(
  tableTop: number,
  tableBottom: number,
  rowCount: number,
  rowSpread: number,
): number[] {
  const tableTopM = tableTop / SVG_UNITS_PER_METRE;
  const tableBottomM = tableBottom / SVG_UNITS_PER_METRE;
  return diningChairRowCentersM(
    tableTopM,
    tableBottomM,
    rowCount,
    rowSpread,
  ).map(metresToSvg);
}

function rectContentHeightSvg(
  tableDepthSvg: number,
  chairRows: number,
): number {
  return metresToSvg(
    diningRectContentHeightM(tableDepthSvg / SVG_UNITS_PER_METRE, chairRows),
  );
}

function finalizeSvg(
  polylines: number[][],
): GeneratedDiningSvg {
  let bb = bboxFromPolylines(polylines);
  const dx = MARGIN_SVG - bb.minX;
  const dy = MARGIN_SVG - bb.minY;
  const shifted = polylines.map((p) => shiftPolyline(p, dx, dy));
  bb = bboxFromPolylines(shifted);

  const contentWidthSvg = bb.width;
  const contentHeightSvg = bb.height;
  const vbW = contentWidthSvg + 2 * MARGIN_SVG;
  const vbH = contentHeightSvg + 2 * MARGIN_SVG;
  const parts = shifted.map((p) => polyline(p));

  return {
    svg: buildSvg(vbW, vbH, parts),
    symLength: +(contentWidthSvg / SVG_UNITS_PER_METRE).toFixed(3),
    symHeight: +(contentHeightSvg / SVG_UNITS_PER_METRE).toFixed(3),
    viewBoxWidth: vbW,
    viewBoxHeight: vbH,
    contentWidthSvg,
    contentHeightSvg,
  };
}

export function generateRectDiningSvg(
  layoutKey: DiningRectLayoutKey,
): GeneratedDiningSvg {
  const layout = DINING_RECT_TABLE_LAYOUTS[layoutKey];
  const size = chairSizeSvg();
  const tableW = metresToSvg(layout.tableWidth);
  const tableD = metresToSvg(layout.tableDepth);
  const gap = metresToSvg(DINING_CHAIR_TABLE_GAP_M);
  const contentW = metresToSvg(diningRectSymbolLengthM(layout.tableWidth));
  const contentH = rectContentHeightSvg(tableD, layout.chairRows);

  const tableLeft = (contentW - tableW) / 2;
  const tableRight = tableLeft + tableW;
  const tableTop = (contentH - tableD) / 2;
  const tableBottom = tableTop + tableD;

  const polylines: number[][] = [
    [
      tableLeft,
      tableTop,
      tableRight,
      tableTop,
      tableRight,
      tableBottom,
      tableLeft,
      tableBottom,
      tableLeft,
      tableTop,
    ],
  ];

  for (const cy of chairRowCenters(
    tableTop,
    tableBottom,
    layout.chairRows,
    layout.chairRowSpread,
  )) {
    const left = sideChairPolylines(
      tableLeft - gap - size.depth / 2,
      cy,
      "left",
      size,
    );
    const right = sideChairPolylines(
      tableRight + gap + size.depth / 2,
      cy,
      "right",
      size,
    );
    polylines.push(left.seat, left.back, right.seat, right.back);
  }

  return finalizeSvg(polylines);
}

/** Four-chair tabletop footprint with the chair glyphs removed. */
export function generatePlainDiningTableSvg(): GeneratedDiningSvg {
  const tableWidth = metresToSvg(DINING_PLAIN_TABLE_LAYOUT.tableWidth);
  const tableDepth = metresToSvg(DINING_PLAIN_TABLE_LAYOUT.tableDepth);

  return finalizeSvg([
    [
      0,
      0,
      tableWidth,
      0,
      tableWidth,
      tableDepth,
      0,
      tableDepth,
      0,
      0,
    ],
  ]);
}

/** Round tabletop footprint with the six chair glyphs removed. */
export function generatePlainRoundDiningTableSvg(): GeneratedDiningSvg {
  const radius = metresToSvg(diningRoundTableRadiusM());
  return finalizeSvg([circlePolyline(radius, radius, radius)]);
}

export function generateRoundDiningSvg(): GeneratedDiningSvg {
  const size = chairSizeSvg();
  const tableRadius = metresToSvg(diningRoundTableRadiusM());
  const gap = metresToSvg(DINING_CHAIR_TABLE_GAP_M);
  const orbit = metresToSvg(diningRoundChairOrbitRadiusM());
  const contentSize = metresToSvg(2 * (diningRoundChairOrbitRadiusM() + DINING_CHAIR_DEPTH_M / 2));
  const tcx = contentSize / 2;
  const tcy = contentSize / 2;

  const polylines: number[][] = [circlePolyline(tcx, tcy, tableRadius)];

  for (let i = 0; i < DINING_ROUND_CHAIR_COUNT; i += 1) {
    const angle = (Math.PI * 2 * i) / DINING_ROUND_CHAIR_COUNT;
    const cx = tcx + Math.cos(angle) * orbit;
    const cy = tcy + Math.sin(angle) * orbit;
    const ch = rotatedChairPolylines(cx, cy, angle, size);
    polylines.push(ch.seat, ch.back);
  }

  return finalizeSvg(polylines);
}

/** Local chair dimensions before rotation (metres). */
export function localChairDimensionsM(): {
  width: number;
  depth: number;
} {
  return {
    width: DINING_CHAIR_WIDTH_M,
    depth: DINING_CHAIR_DEPTH_M,
  };
}

/** Library metadata derived from generated SVG content bbox (metres). */
export const DINING_SYMBOL_LIBRARY_DIMENSIONS = {
  "MANUAL/plain-table": generatePlainDiningTableSvg(),
  "MANUAL/plain-round-table": generatePlainRoundDiningTableSvg(),
  "MANUAL/2-chair-table": generateRectDiningSvg("2-chair"),
  "MANUAL/4-chair-table": generateRectDiningSvg("4-chair"),
  "MANUAL/6-chair-table": generateRectDiningSvg("6-chair"),
  "MANUAL/round-table": generateRoundDiningSvg(),
} as const;
